/**
 * Configuration for the Microsoft OCV In-App Feedback entry point in the header.
 *
 * The OCV `appId` and `clientName` are provisioned per application through the
 * Microsoft OCV onboarding process. Until an `appId` is configured via
 * `VITE_FEEDBACK_APP_ID`, {@link FeedbackConfig.isEnabled} is `false` and the
 * feedback entry point is hidden — the feature is otherwise fully wired.
 *
 * Environment variables (see `env/.env.template`):
 *   - `VITE_FEEDBACK_APP_ID`     — numeric OCV App Id (required to enable)
 *   - `VITE_FEEDBACK_CLIENT_NAME`— OCV client name (defaults to "ArriveKiosk")
 *   - `VITE_FEEDBACK_HOST_NAME`  — OCV host name (defaults to "ArriveKiosk")
 */
export interface FeedbackConfig {
  /** Numeric OCV App Id, or `undefined` when not provisioned. */
  readonly appId: number | undefined;
  /** OCV client name reported to the feedback SDK. */
  readonly clientName: string;
  /** OCV host name reported to the feedback SDK. */
  readonly hostName: string;
  /** Whether the SDK should run in production (non-INT) mode. */
  readonly isProduction: boolean;
  /** `true` only when a valid numeric `appId` is configured. */
  readonly isEnabled: boolean;
}

const DEFAULT_CLIENT_NAME = 'ArriveKiosk';
const DEFAULT_HOST_NAME = 'ArriveKiosk';

function normalize(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

/**
 * Resolves the feedback configuration from the current environment. Reading is cheap
 * and side-effect free, so callers may invoke it on each render.
 */
export function getFeedbackConfig(): FeedbackConfig {
  const rawAppId = normalize(import.meta.env.VITE_FEEDBACK_APP_ID);
  const appId = rawAppId && /^\d+$/.test(rawAppId) ? Number(rawAppId) : undefined;

  return {
    appId,
    clientName: normalize(import.meta.env.VITE_FEEDBACK_CLIENT_NAME) ?? DEFAULT_CLIENT_NAME,
    hostName: normalize(import.meta.env.VITE_FEEDBACK_HOST_NAME) ?? DEFAULT_HOST_NAME,
    isProduction: import.meta.env.VITE_ENV_NAME === 'prod',
    isEnabled: appId !== undefined,
  };
}
